import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {lastValueFrom} from 'rxjs';

/**
 * Downloads authenticated images once (the auth interceptor adds the Bearer token) and caches them as
 * base64 data URLs so they can be bound directly to an <img>/p-avatar. Keyed by the image URL.
 */
@Injectable({
  providedIn: 'root',
})
export class ImageCacheService {
  private readonly http = inject(HttpClient);

  // Cache the in-flight Promise (not a string): concurrent callers share it, and no polling is needed.
  private readonly cache = new Map<string, Promise<string>>();

  getBase64Image(imageUrl: string): Promise<string> {
    const cached = this.cache.get(imageUrl);
    if (cached) {
      return cached;
    }

    const promise = lastValueFrom(this.http.get(imageUrl, {responseType: 'blob'}))
      .then(blob => this.toDataUrl(blob))
      .catch(error => {
        // Don't poison the cache on failure, otherwise the URL would stay broken for the whole session.
        this.cache.delete(imageUrl);
        throw error;
      });

    this.cache.set(imageUrl, promise);
    return promise;
  }

  // Called on logout to drop cached avatars, bounding memory and avoiding image bleed across sessions.
  clear(): void {
    this.cache.clear();
  }

  private toDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      // reader.result already carries the correct "data:<mime>;base64,..." prefix, keep it as-is.
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }
}
